import React, { useState, useRef } from 'react';
import { aiService } from '../../services/calorieAI/aiService';

const ModelTesting = () => {
  const [selectedModel, setSelectedModel] = useState(0);
  const [testImage, setTestImage] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const fileInputRef = useRef(null);

  const availableModels = aiService.getAvailableModelsWithDetails();

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTestImage(file);
      // 清除之前的測試結果
      setTestResults([]);
    }
  };

  const testSingleModel = async (modelIndex) => {
    if (!testImage) {
      alert('請先選擇一張測試圖片');
      return;
    }

    setIsLoading(true);
    setCurrentTest(modelIndex);
    
    try {
      const startTime = Date.now();
      
      // 切換到指定模型
      aiService.setModel(modelIndex);
      
      // 執行食物識別
      const result = await aiService.identifyFood(testImage, '請詳細分析這張圖片中的食物');
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      const testResult = {
        modelIndex,
        modelName: availableModels[modelIndex].displayName,
        result,
        responseTime,
        timestamp: new Date().toLocaleString(),
        success: true
      };

      setTestResults(prev => [...prev, testResult]);
      
    } catch (error) {
      console.error(`模型 ${availableModels[modelIndex].displayName} 測試失敗:`, error);
      
      const testResult = {
        modelIndex,
        modelName: availableModels[modelIndex].displayName,
        error: error.message,
        timestamp: new Date().toLocaleString(),
        success: false
      };

      setTestResults(prev => [...prev, testResult]);
    } finally {
      setIsLoading(false);
      setCurrentTest(null);
    }
  };

  const testAllModels = async () => {
    if (!testImage) {
      alert('請先選擇一張測試圖片');
      return;
    }

    setTestResults([]);
    
    for (let i = 0; i < availableModels.length; i++) {
      await testSingleModel(i);
      // 在測試之間添加短暫延遲，避免 API 限制
      if (i < availableModels.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

  const clearResults = () => {
    setTestResults([]);
    setTestImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const exportResults = () => {
    const dataStr = JSON.stringify(testResults, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `model_test_results_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="model-testing-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>🧪 AI 模型效能測試</h2>
      
      {/* 圖片上傳區域 */}
      <div style={{ 
        border: '2px dashed #3498db', 
        borderRadius: '10px', 
        padding: '20px', 
        textAlign: 'center',
        marginBottom: '20px',
        backgroundColor: '#f8f9fa'
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ marginBottom: '10px' }}
        />
        {testImage && (
          <div style={{ marginTop: '10px' }}>
            <p style={{ color: '#27ae60', fontWeight: 'bold' }}>
              ✅ 已選擇圖片: {testImage.name}
            </p>
            <img 
              src={URL.createObjectURL(testImage)} 
              alt="測試圖片" 
              style={{ 
                maxWidth: '200px', 
                maxHeight: '200px', 
                objectFit: 'cover',
                borderRadius: '8px',
                border: '1px solid #ddd'
              }} 
            />
          </div>
        )}
      </div>

      {/* 控制按鈕 */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={testAllModels}
          disabled={isLoading || !testImage}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading || !testImage ? 0.6 : 1
          }}
        >
          {isLoading ? '測試中...' : '🚀 測試所有模型'}
        </button>

        {availableModels.map((model, index) => (
          <button
            key={index}
            onClick={() => testSingleModel(index)}
            disabled={isLoading || !testImage}
            style={{
              padding: '8px 16px',
              backgroundColor: currentTest === index ? '#e74c3c' : '#2ecc71',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading || !testImage ? 0.6 : 1,
              fontSize: '12px'
            }}
          >
            {currentTest === index ? '測試中...' : `測試 ${model.displayName}`}
          </button>
        ))}

        <button
          onClick={clearResults}
          style={{
            padding: '10px 20px',
            backgroundColor: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          🗑️ 清除結果
        </button>

        {testResults.length > 0 && (
          <button
            onClick={exportResults}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            📊 匯出結果
          </button>
        )}
      </div>

      {/* 測試結果顯示 */}
      {testResults.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>📊 測試結果</h3>
          
          {/* 結果摘要 */}
          <div style={{ 
            backgroundColor: '#ecf0f1', 
            padding: '15px', 
            borderRadius: '8px', 
            marginBottom: '20px' 
          }}>
            <h4>📈 效能摘要</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              {testResults.filter(r => r.success).map((result, index) => (
                <div key={index} style={{ 
                  backgroundColor: 'white', 
                  padding: '10px', 
                  borderRadius: '5px',
                  border: '1px solid #bdc3c7'
                }}>
                  <strong>{result.modelName}</strong>
                  <br />
                  <span style={{ color: '#27ae60' }}>⏱️ {result.responseTime}ms</span>
                  <br />
                  <span style={{ color: '#3498db' }}>
                    🍽️ {result.result?.foods?.length || 0} 項食物
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 詳細結果 */}
          {testResults.map((result, index) => (
            <div key={index} style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '15px', 
              marginBottom: '15px',
              backgroundColor: result.success ? '#f8fff8' : '#fff8f8'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <h4 style={{ 
                  color: result.success ? '#27ae60' : '#e74c3c',
                  margin: 0
                }}>
                  {result.success ? '✅' : '❌'} {result.modelName}
                </h4>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  {result.timestamp}
                  {result.success && (
                    <span style={{ marginLeft: '10px', color: '#3498db' }}>
                      ⏱️ {result.responseTime}ms
                    </span>
                  )}
                </div>
              </div>

              {result.success ? (
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>識別結果:</strong>
                    <div style={{ 
                      backgroundColor: 'white', 
                      padding: '10px', 
                      borderRadius: '5px',
                      border: '1px solid #e0e0e0',
                      marginTop: '5px'
                    }}>
                      {result.result.foods?.map((food, foodIndex) => (
                        <div key={foodIndex} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                          <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                            🍽️ {food.name} ({food.quantity} {food.unit})
                          </div>
                          <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '2px' }}>
                            📊 {food.calories}卡 | 🥩 {food.protein}g蛋白質 | 🍞 {food.carbs}g碳水 | 🥑 {food.fat}g脂肪
                          </div>
                          <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                            信心度: {(food.confidence * 100).toFixed(1)}% | 健康指數: {food.healthyIndex}/5
                          </div>
                        </div>
                      ))}
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        color: '#2c3e50'
                      }}>
                        總卡路里: {result.result.total_calories}卡
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#e74c3c' }}>
                  <strong>錯誤訊息:</strong> {result.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 使用說明 */}
      <div style={{ 
        marginTop: '30px', 
        padding: '15px', 
        backgroundColor: '#e8f4fd', 
        borderRadius: '8px',
        border: '1px solid #3498db'
      }}>
        <h4 style={{ color: '#2c3e50', marginTop: 0 }}>📝 使用說明</h4>
        <ul style={{ color: '#34495e', paddingLeft: '20px' }}>
          <li>選擇一張包含食物的圖片進行測試</li>
          <li>可以單獨測試某個模型，或一次測試所有模型</li>
          <li>比較各模型的回應時間、準確度和識別結果</li>
          <li>可以匯出測試結果為 JSON 檔案進行進一步分析</li>
          <li>建議使用不同類型的食物圖片進行全面測試</li>
        </ul>
      </div>
    </div>
  );
};

export default ModelTesting;